// File: utils/helpers.js

/**
 * Helper Functions
 * 
 * Common utility functions used throughout the application.
 * Pure functions with no side effects.
 */

import { READING, DATE_FORMATS } from './constants.js';

// ========================================
// String Helpers
// ========================================

/**
 * Capitalize first letter of string
 */
export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Truncate string to max length with ellipsis
 */
export function truncate(str, maxLength = 100) {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength).trim() + '...';
}

/**
 * Remove HTML tags from string
 */
export function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

/**
 * Convert string to slug (URL-friendly)
 */
export function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Escape HTML entities to prevent XSS
 */
export function escapeHtml(str) {
  if (!str) return '';
  const htmlEntities = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };
  return str.replace(/[&<>"']/g, char => htmlEntities[char]);
}

/**
 * Highlight search terms in text (XSS-safe)
 */
export function highlightText(text, searchTerm, className = 'highlight') {
  if (!text || !searchTerm) return escapeHtml(text) || text;

  // Escape HTML in the text first to prevent XSS
  const escapedText = escapeHtml(text);
  const escapedSearchTerm = escapeHtml(searchTerm);

  const regex = new RegExp(`(${escapeRegex(escapedSearchTerm)})`, 'gi');
  return escapedText.replace(regex, `<span class="${className}">$1</span>`);
}

/**
 * Escape special regex characters
 */
export function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ========================================
// Number Helpers
// ========================================

/**
 * Format number with commas
 */
export function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Generate random ID
 */
export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Calculate percentage
 */
export function calculatePercentage(value, total) {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

// ========================================
// Array Helpers
// ========================================

/**
 * Remove duplicates from array
 */
export function uniqueArray(arr) {
  return [...new Set(arr)];
}

/**
 * Shuffle array
 */
export function shuffleArray(arr) {
  const newArr = [...arr];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
}

/**
 * Group array by key
 */
export function groupBy(arr, key) {
  return arr.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(item);
    return result;
  }, {});
}

/**
 * Sort array by key
 */
export function sortBy(arr, key, direction = 'asc') {
  return [...arr].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (direction === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });
}

// ========================================
// Object Helpers
// ========================================

/**
 * Deep clone object
 */
export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if object is empty
 */
export function isEmpty(obj) {
  if (obj == null) return true;
  if (Array.isArray(obj) || typeof obj === 'string') return obj.length === 0;
  return Object.keys(obj).length === 0;
}

/**
 * Get nested property safely
 */
export function getNestedProperty(obj, path, defaultValue = null) {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result == null || typeof result !== 'object') {
      return defaultValue;
    }
    result = result[key];
  }
  
  return result !== undefined ? result : defaultValue;
}

/**
 * Merge objects deeply
 */
export function deepMerge(target, ...sources) {
  if (!sources.length) return target;
  const source = sources.shift();
  
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  
  return deepMerge(target, ...sources);
}

function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

// ========================================
// Date Helpers
// ========================================

/**
 * Format date to readable string
 */
export function formatDate(date, format = DATE_FORMATS.SHORT) {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthsFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();
  
  switch (format) {
    case DATE_FORMATS.SHORT:
      return `${months[month]} ${day}, ${year}`;
    case DATE_FORMATS.LONG:
      return `${monthsFull[month]} ${day}, ${year}`;
    case DATE_FORMATS.ISO:
      return d.toISOString().split('T')[0];
    case DATE_FORMATS.DISPLAY:
      return `${months[month]} ${day}`;
    default:
      return d.toLocaleDateString();
  }
}

/**
 * Get relative time (e.g., "2 days ago")
 */
export function getRelativeTime(date) {
  if (!date) return '';
  
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  if (diffMonth < 12) return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
  return `${diffYear} year${diffYear > 1 ? 's' : ''} ago`;
}

/**
 * Check if date is today
 */
export function isToday(date) {
  const today = new Date();
  const d = new Date(date);
  return d.getDate() === today.getDate() &&
         d.getMonth() === today.getMonth() &&
         d.getFullYear() === today.getFullYear();
}

// ========================================
// Reading Time Helpers
// ========================================

/**
 * Calculate reading time from word count
 */
export function calculateReadingTime(wordCount) {
  return READING.CALCULATE_TIME(wordCount);
}

/**
 * Count words in text
 */
export function countWords(text) {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * Format reading time
 */
export function formatReadingTime(minutes) {
  if (minutes < 1) return 'Less than 1 min';
  if (minutes === 1) return '1 min read';
  return `${minutes} min read`;
}

// ========================================
// URL Helpers
// ========================================

/**
 * Build query string from object
 */
export function buildQueryString(params) {
  return Object.keys(params)
    .filter(key => params[key] != null)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
}

/**
 * Parse query string to object
 */
export function parseQueryString(queryString) {
  if (!queryString) return {};
  
  return queryString
    .replace(/^\?/, '')
    .split('&')
    .reduce((params, param) => {
      const [key, value] = param.split('=').map(decodeURIComponent);
      if (key) params[key] = value;
      return params;
    }, {});
}

/**
 * Get current URL parameters
 */
export function getUrlParams() {
  return parseQueryString(window.location.search);
}

// ========================================
// DOM Helpers
// ========================================

/**
 * Scroll to element smoothly
 */
export function scrollToElement(element, offset = 0) {
  if (!element) return;
  
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;
  
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
}

/**
 * Scroll to top of page
 */
export function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}

/**
 * Check if element is in viewport
 */
export function isInViewport(element) {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Get scroll position
 */
export function getScrollPosition() {
  return window.pageYOffset || document.documentElement.scrollTop;
}

// ========================================
// Debounce & Throttle
// ========================================

/**
 * Debounce function execution
 */
export function debounce(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function execution
 */
export function throttle(func, limit = 300) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// ========================================
// Validation Helpers
// ========================================

/**
 * Check if string is valid email
 */
export function isValidEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}

/**
 * Check if string is valid URL
 */
export function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// ========================================
// Copy to Clipboard
// ========================================

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

// ========================================
// Device Detection
// ========================================

/**
 * Check if device is mobile
 */
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Check if device is iOS
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Check if device is Android
 */
export function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

/**
 * Get device type
 */
export function getDeviceType() {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  if (isMobile()) return 'mobile';
  return 'desktop';
}

// ========================================
// Browser Detection
// ========================================

/**
 * Check if browser is Safari
 */
export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

/**
 * Check if browser supports service workers
 */
export function supportsServiceWorker() {
  return 'serviceWorker' in navigator;
}

// ========================================
// Export All
// ========================================

const helpers = {
  // String
  capitalize,
  truncate,
  stripHtml,
  slugify,
  escapeHtml,
  highlightText,
  escapeRegex,
  
  // Number
  formatNumber,
  generateId,
  calculatePercentage,
  
  // Array
  uniqueArray,
  shuffleArray,
  groupBy,
  sortBy,
  
  // Object
  deepClone,
  isEmpty,
  getNestedProperty,
  deepMerge,
  
  // Date
  formatDate,
  getRelativeTime,
  isToday,
  
  // Reading
  calculateReadingTime,
  countWords,
  formatReadingTime,
  
  // URL
  buildQueryString,
  parseQueryString,
  getUrlParams,
  
  // DOM
  scrollToElement,
  scrollToTop,
  isInViewport,
  getScrollPosition,
  
  // Performance
  debounce,
  throttle,
  
  // Validation
  isValidEmail,
  isValidUrl,
  
  // Clipboard
  copyToClipboard,
  
  // Device
  isMobile,
  isIOS,
  isAndroid,
  getDeviceType,
  isSafari,
  supportsServiceWorker,
};

export default helpers;
